/**
 * S-02 日報入力（1日ずつ）。
 *
 * プレビューはサーバーと同じ計算エンジン（@meisui/core）をブラウザで実行する。
 * 保存時はサーバー側で再計算するため、表示と保存結果は必ず一致する。
 */

import {
  addDays,
  computeDaily,
  DEFAULT_WEEKDAY_BREAK_MINUTES,
  HOLIDAY_WORK_TREATMENTS,
  validateDaily,
  WORK_PATTERN_LABELS,
  WORK_TYPES,
  type DailyInput,
  type DayType,
  type HolidayWorkTreatment,
  type WorkType,
} from '@meisui/core';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { endpoints } from '../../api/endpoints.js';
import type { DailyResponse } from '../../api/types.js';
import { useMe } from '../../auth/context.js';
import { NumberField, SelectField, TextField, TimeField } from '../../components/TimeField.js';
import {
  CalcPanel,
  Card,
  ErrorMessage,
  IssueList,
  Loading,
  Notice,
  StatusPill,
} from '../../components/ui.js';
import { formatDateLabel, formatDateTime, formatDuration, todayIso } from '../../lib/format.js';
import { useAsync } from '../../lib/useAsync.js';
import { useTimeDrafts } from '../../lib/useTimeDrafts.js';
import { useUnsavedChanges } from '../../lib/unsaved-context.js';
import { Modal } from '../../components/Modal.js';

type FormState = Omit<DailyInput, 'date' | 'dayType'>;

const EMPTY_FORM: FormState = {
  workType: '出勤',
  directStart: null,
  officeIn: null,
  workStart: null,
  workEnd: null,
  officeOut: null,
  directHome: null,
  breakTotalMinutes: null,
  hourlyLeaveHours: 0,
  breakOverrideMinutes: null,
  holidayWorkTreatment: null,
  siteName: '',
  taskNote: '',
  remarks: '',
};

function toForm(response: DailyResponse): FormState {
  const report = response.report;
  if (!report) {
    return {
      ...EMPTY_FORM,
      // 平日は休憩90分が既定。休日は実績を必ず入力してもらうため空のままにする
      breakTotalMinutes: response.dayType === '平日' ? DEFAULT_WEEKDAY_BREAK_MINUTES : null,
      workType: response.dayType === '平日' ? '出勤' : '休日出勤',
    };
  }
  return {
    workType: report.workType,
    directStart: report.directStart,
    officeIn: report.officeIn,
    workStart: report.workStart,
    workEnd: report.workEnd,
    officeOut: report.officeOut,
    directHome: report.directHome,
    breakTotalMinutes: report.breakTotalMinutes,
    hourlyLeaveHours: report.hourlyLeaveHours,
    breakOverrideMinutes: report.breakOverrideMinutes,
    holidayWorkTreatment: report.holidayWorkTreatment,
    siteName: report.siteName,
    taskNote: report.taskNote,
    remarks: report.remarks,
  };
}

export function DailyEntryPage(): React.JSX.Element {
  const me = useMe();
  const navigate = useNavigate();
  const params = useParams<{ date?: string }>();
  const date = params.date ?? todayIso();

  const state = useAsync(() => endpoints.daily(date), [date]);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState<'draft' | 'submit' | 'withdraw' | 'auto' | 'copy' | null>(
    null,
  );
  const savingRef = useRef(false);
  const times = useTimeDrafts();
  const [morning, setMorning] = useState<'office' | 'direct' | 'both'>('office');
  const [evening, setEvening] = useState<'office' | 'direct' | 'both'>('office');
  const [travelOnly, setTravelOnly] = useState(false);
  const [pendingEdit, setPendingEdit] = useState<{ message: string; apply: () => void } | null>(
    null,
  );
  const [showIssues, setShowIssues] = useState(false);
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const [message, setMessage] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<unknown>(null);
  const [autoSavedAt, setAutoSavedAt] = useState<Date | null>(null);
  /** 保存済みの内容。これと違えば未保存の変更があるとみなす */
  const [savedForm, setSavedForm] = useState<FormState>(EMPTY_FORM);
  /** 楽観ロック用。自動保存でも進むため、画面の読み込み値ではなく保存結果を持ち回す */
  const revision = useRef<number | null>(null);
  /** いま入力欄に出している内容がどの日のものか。読み込み中の日付切替で取り違えないようにする */
  const [formDate, setFormDate] = useState<string | null>(null);

  useEffect(() => {
    if (state.data) {
      const loaded = toForm(state.data);
      setForm(loaded);
      times.reset();
      setMorning(
        loaded.directStart !== null ? (loaded.officeIn !== null ? 'both' : 'direct') : 'office',
      );
      setEvening(
        loaded.directHome !== null ? (loaded.officeOut !== null ? 'both' : 'direct') : 'office',
      );
      setTravelOnly(
        loaded.directStart !== null &&
          loaded.directHome !== null &&
          loaded.workStart === null &&
          loaded.workEnd === null &&
          loaded.officeIn === null &&
          loaded.officeOut === null,
      );
      setTouched({});
      setShowIssues(false);
      setSavedForm(loaded);
      revision.current = state.data.report?.revision ?? null;
      setFormDate(state.data.date);
    }
    // Reset the editable view only when a server response arrives.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.data]);

  // 保存後の読み直しで消えないよう、知らせの初期化は日付が変わったときだけ行う
  useEffect(() => {
    setMessage(null);
    setSaveError(null);
    setAutoSavedAt(null);
  }, [date]);

  const dayType: DayType = state.data?.dayType ?? '平日';
  const input: DailyInput = useMemo(() => ({ ...form, date, dayType }), [form, date, dayType]);
  const computed = useMemo(() => computeDaily(input), [input]);
  const issues = useMemo(() => validateDaily(input, computed), [input, computed]);

  const report = state.data?.report ?? null;
  const submitted = report?.status === 'submitted';
  // 提出済みは取り消してから編集する（画面仕様書 §2.3）。承認済み・確定済みは本人は編集できない
  const readOnly =
    (state.data?.closed ?? false) ||
    (report?.locked ?? false) ||
    report?.status === 'approved' ||
    submitted;
  const hasError = times.hasErrors || issues.some((issue) => issue.severity === 'error');
  const inputDisabled = readOnly || saving !== null || state.loading;
  const works =
    ['出勤', '休日出勤', '時間年休'].includes(form.workType) ||
    ['directStart', 'officeIn', 'workStart', 'workEnd', 'officeOut', 'directHome'].some(
      (key) => form[key as keyof FormState] !== null,
    );
  const dirty = times.hasErrors || JSON.stringify(form) !== JSON.stringify(savedForm);

  const inSync = state.data !== null && formDate === date;
  const canAutoSave =
    inSync && !readOnly && saving === null && dirty && !times.hasErrors && pendingEdit === null;

  const set = <K extends keyof FormState>(key: K, value: FormState[K]): void => {
    setForm((previous) => ({ ...previous, [key]: value }));
    setMessage(null);
  };

  const save = async (submit: boolean, automatic = false): Promise<boolean> => {
    if (!inSync || readOnly || savingRef.current) return false;
    if (times.hasErrors || (submit && hasError)) {
      setShowIssues(true);
      setSaveError(new Error('入力エラーを修正してください。時刻は08:30のように入力します。'));
      return false;
    }
    if (
      submit &&
      issues.length > 0 &&
      !window.confirm(
        `確認事項が${issues.length}件あります。このまま提出しますか？\n\n${issues.map((issue) => `・${issue.message}`).join('\n')}`,
      )
    )
      return false;
    savingRef.current = true;
    setSaving(automatic ? 'auto' : submit ? 'submit' : 'draft');
    setSaveError(null);
    try {
      const result = await endpoints.saveDaily(date, {
        ...form,
        submit,
        revision: revision.current,
      });
      setSavedForm(form);
      revision.current = result.report.revision;
      if (automatic) setAutoSavedAt(new Date());
      else {
        setMessage(
          submit ? '提出しました。上長の承認をお待ちください。' : '下書きを保存しました。',
        );
        state.reload();
      }
      return true;
    } catch (cause) {
      setSaveError(cause);
      return false;
    } finally {
      savingRef.current = false;
      setSaving(null);
    }
  };

  const navigationPending = useUnsavedChanges({
    dirty: !readOnly && dirty,
    saving: saving !== null,
    save: () => (dirty ? save(false) : Promise.resolve(true)),
  });

  useEffect(() => {
    if (!canAutoSave || navigationPending) return;
    const timer = window.setTimeout(() => void save(false, true), 30_000);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAutoSave, navigationPending, form]);

  const changePattern = (side: 'morning' | 'evening', mode: 'office' | 'direct'): void => {
    const cleared =
      side === 'morning'
        ? mode === 'office'
          ? 'directStart'
          : 'officeIn'
        : mode === 'office'
          ? 'directHome'
          : 'officeOut';
    const apply = (): void => {
      set(cleared, null);
      times.clear([cleared]);
      if (side === 'morning') setMorning(mode);
      else setEvening(mode);
      setPendingEdit(null);
    };
    if (form[cleared] !== null || times.hasErrors)
      setPendingEdit({
        message:
          '切り替えると、選択しない側の時刻を消します。入力内容を確認して切り替えてください。',
        apply,
      });
    else apply();
  };

  const changeWorkType = (workType: WorkType): void => {
    const nextWorks = ['出勤', '休日出勤', '時間年休'].includes(workType);
    const apply = (): void => {
      if (!nextWorks) {
        setForm((previous) => ({
          ...previous,
          workType,
          directStart: null,
          officeIn: null,
          workStart: null,
          workEnd: null,
          officeOut: null,
          directHome: null,
          breakTotalMinutes: null,
          breakOverrideMinutes: null,
          hourlyLeaveHours: 0,
          holidayWorkTreatment: null,
        }));
        times.reset();
        setTravelOnly(false);
        setMorning('office');
        setEvening('office');
      } else set('workType', workType);
      setPendingEdit(null);
    };
    if (
      !nextWorks &&
      (['directStart', 'officeIn', 'workStart', 'workEnd', 'officeOut', 'directHome'].some(
        (key) => form[key as keyof FormState] !== null,
      ) ||
        form.hourlyLeaveHours > 0 ||
        form.breakOverrideMinutes !== null ||
        form.holidayWorkTreatment !== null ||
        (form.breakTotalMinutes !== null &&
          form.breakTotalMinutes !== (dayType === '平日' ? DEFAULT_WEEKDAY_BREAK_MINUTES : null)) ||
        times.hasErrors)
    ) {
      setPendingEdit({
        message:
          '休暇・欠勤に切り替えると、勤務時刻・休憩・時間単位年休・休日出勤の取扱いを消します。業務内容と備考は残ります。',
        apply,
      });
    } else apply();
  };

  const changeTravelOnly = (checked: boolean): void => {
    const apply = (): void => {
      if (checked) {
        setForm((previous) => ({
          ...previous,
          officeIn: null,
          officeOut: null,
          workStart: null,
          workEnd: null,
        }));
        times.clear(['officeIn', 'officeOut', 'workStart', 'workEnd']);
        setMorning('direct');
        setEvening('direct');
      }
      setTravelOnly(checked);
      setPendingEdit(null);
    };
    if (
      checked &&
      (form.officeIn !== null ||
        form.officeOut !== null ||
        form.workStart !== null ||
        form.workEnd !== null ||
        times.hasErrors)
    )
      setPendingEdit({
        message:
          '移動のみに切り替えると、出社・退社・始業・終業の時刻を消します。直行・直帰の時刻は残ります。',
        apply,
      });
    else apply();
  };

  const withdraw = async (): Promise<void> => {
    setSaving('withdraw');
    setSaveError(null);
    try {
      await endpoints.withdraw(date);
      setMessage('提出を取り消しました。');
      state.reload();
    } catch (cause) {
      setSaveError(cause);
    } finally {
      setSaving(null);
    }
  };

  const copyPreviousDay = async (): Promise<void> => {
    if (inputDisabled || times.hasErrors || !works || savingRef.current) return;
    savingRef.current = true;
    setSaving('copy');
    try {
      const previous = await endpoints.daily(addDays(date, -1));
      if (!previous.report) {
        setMessage('前日の日報が見つかりませんでした。');
        return;
      }
      const source = toForm(previous);
      setForm((current) => ({
        ...current,
        directStart: source.directStart,
        officeIn: source.officeIn,
        workStart: source.workStart,
        workEnd: source.workEnd,
        officeOut: source.officeOut,
        directHome: source.directHome,
        breakTotalMinutes: source.breakTotalMinutes,
      }));
      times.reset();
      setMorning(
        source.directStart !== null ? (source.officeIn !== null ? 'both' : 'direct') : 'office',
      );
      setEvening(
        source.directHome !== null ? (source.officeOut !== null ? 'both' : 'direct') : 'office',
      );
      setTravelOnly(
        source.directStart !== null &&
          source.directHome !== null &&
          source.workStart === null &&
          source.workEnd === null &&
          source.officeIn === null &&
          source.officeOut === null,
      );
      setMessage('前日の勤務時刻をコピーしました。');
    } catch (cause) {
      setSaveError(cause);
    } finally {
      savingRef.current = false;
      setSaving(null);
    }
  };

  if (state.loading && !state.data) return <Loading />;
  if (state.error) return <ErrorMessage error={state.error} />;
  if (!state.data || formDate !== date) return <Loading />;

  const isHoliday = dayType !== '平日';

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="page-heading">{formatDateLabel(date)}の日報</h1>
          <p className="flex items-center gap-2 text-sm text-ink-muted">
            <span className="pill pill--muted">{dayType}</span>
            <StatusPill status={report?.status ?? null} locked={report?.locked} />
          </p>
        </div>
        <div className="flex gap-2">
          <Link className="btn btn--secondary btn--sm" to={`/daily/${addDays(date, -1)}`}>
            前日
          </Link>
          <Link className="btn btn--secondary btn--sm" to={`/daily/${addDays(date, 1)}`}>
            翌日
          </Link>
        </div>
      </div>

      {state.data.closed && (
        <Notice tone="info" title="この月は確定済みです">
          内容の変更はできません。修正が必要な場合は管理部へ連絡してください。
        </Notice>
      )}
      {report?.status === 'approved' && !state.data.closed && (
        <Notice tone="info" title="承認済みです">
          修正が必要な場合は管理部に承認の取消を依頼してください。
        </Notice>
      )}
      {report?.status === 'rejected' && (
        <Notice tone="warn" title="差戻しされています">
          {report.rejectionReason || '理由の記載はありません。'}
        </Notice>
      )}
      {submitted && !state.data.closed && (
        <Notice tone="info" title="提出済みです">
          上長の承認をお待ちください。修正する場合は「提出を取り消す」を押してから編集します。
        </Notice>
      )}
      {message !== null && <Notice tone="info">{message}</Notice>}
      <ErrorMessage error={saveError} />

      <div className="grid gap-4 lg:grid-cols-[1fr_340px]">
        <div className="space-y-4">
          <Card
            title="勤務区分"
            step={1}
            action={
              !readOnly && (
                <button
                  type="button"
                  className="btn btn--secondary btn--sm"
                  disabled={inputDisabled || times.hasErrors || !works}
                  onClick={() =>
                    setPendingEdit({
                      message:
                        '前日の勤務時刻と休憩合計をコピーし、現在の入力を置き換えます。業務内容は変わりません。',
                      apply: () => {
                        setPendingEdit(null);
                        void copyPreviousDay();
                      },
                    })
                  }
                >
                  前日をコピー
                </button>
              )
            }
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <SelectField
                label="勤務種別"
                value={form.workType}
                options={WORK_TYPES}
                disabled={inputDisabled}
                onChange={(value) => changeWorkType((value ?? '出勤') as WorkType)}
              />
              {form.workType === '休日出勤' && (
                <SelectField
                  label="休日出勤の取扱い"
                  value={form.holidayWorkTreatment}
                  options={HOLIDAY_WORK_TREATMENTS}
                  placeholder="選択してください"
                  disabled={inputDisabled}
                  hint="代休・振替に充当できるのは8時間までです"
                  onChange={(value) =>
                    set('holidayWorkTreatment', value as HolidayWorkTreatment | null)
                  }
                />
              )}
              <div>
                <span className="field-label">業務パターン</span>
                <p className="text-[0.9rem] font-semibold">
                  {computed.pattern
                    ? `${computed.pattern} ${WORK_PATTERN_LABELS[computed.pattern]}`
                    : '—'}
                </p>
                <p className="field-hint">入力した時刻から自動で判定します。</p>
              </div>
            </div>
          </Card>

          {works ? (
            <Card
              title="勤務時刻"
              step={2}
              description="行き・帰りの方法を選び、表示された時刻を入力します。"
            >
              <fieldset
                disabled={inputDisabled}
                className="space-y-4"
                onBlur={(event) => {
                  const field = (event.target as HTMLElement)
                    .closest('[data-time-field]')
                    ?.getAttribute('data-time-field');
                  if (field) setTouched((previous) => ({ ...previous, [field]: true }));
                }}
              >
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={travelOnly}
                    onChange={(event) => changeTravelOnly(event.target.checked)}
                  />
                  移動のみ（現場での作業なし）
                </label>
                {!travelOnly && (
                  <div className="grid sm:grid-cols-2 gap-4">
                    {(['morning', 'evening'] as const).map((side) => (
                      <div key={side}>
                        <span className="field-label">{side === 'morning' ? '行き' : '帰り'}</span>
                        <div
                          className="flex gap-2"
                          role="group"
                          aria-label={side === 'morning' ? '行きの方法' : '帰りの方法'}
                        >
                          {(['office', 'direct'] as const).map((mode) => (
                            <button
                              type="button"
                              key={mode}
                              aria-pressed={(side === 'morning' ? morning : evening) === mode}
                              className={`btn flex-1 ${(side === 'morning' ? morning : evening) === mode ? 'btn--primary' : 'btn--secondary'}`}
                              onClick={() => changePattern(side, mode)}
                            >
                              {side === 'morning'
                                ? mode === 'office'
                                  ? '出社'
                                  : '直行'
                                : mode === 'office'
                                  ? '退社'
                                  : '直帰'}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="grid gap-4 sm:grid-cols-2">
                  {(
                    [
                      {
                        key: 'directStart',
                        label: '①直行',
                        show: morning !== 'office',
                        hint: '自宅などから現場へ向かった時刻',
                      },
                      {
                        key: 'officeIn',
                        label: '②出社',
                        show: !travelOnly && morning !== 'direct',
                        hint: '事務所に到着した時刻',
                      },
                      { key: 'workStart', label: '③始業', show: !travelOnly },
                      { key: 'workEnd', label: '④終業', show: !travelOnly },
                      {
                        key: 'officeOut',
                        label: '⑤退社',
                        show: !travelOnly && evening !== 'direct',
                      },
                      {
                        key: 'directHome',
                        label: '⑥直帰',
                        show: evening !== 'office',
                        hint: '自宅などに到着した時刻',
                      },
                    ] as const
                  )
                    .filter((field) => field.show)
                    .map((field) => (
                      <div key={field.key} data-time-field={field.key}>
                        <TimeField
                          label={field.label}
                          disabled={inputDisabled}
                          showErrors={showIssues}
                          {...times.bind(field.key, form[field.key], (value) =>
                            set(field.key, value),
                          )}
                          hint={
                            'hint' in field
                              ? field.hint
                              : '時刻を選択、または830のように直接入力できます'
                          }
                          error={
                            showIssues || touched[field.key]
                              ? issues.find((issue) => issue.field === field.key)?.message
                              : undefined
                          }
                        />
                      </div>
                    ))}
                  <div>
                    <NumberField
                      label="⑦休憩合計"
                      unit="分"
                      value={form.breakTotalMinutes}
                      disabled={inputDisabled}
                      onChange={(value) => set('breakTotalMinutes', value)}
                      hint={isHoliday ? '休日は実績を入力します' : '平日の既定は90分'}
                    />
                    <div className="mt-2 flex gap-2">
                      {[45, 60, 90].map((minutes) => (
                        <button
                          type="button"
                          key={minutes}
                          className="btn btn--secondary btn--sm"
                          onClick={() => set('breakTotalMinutes', minutes)}
                        >
                          {minutes}分
                        </button>
                      ))}
                    </div>
                  </div>
                  <label>
                    <span className="field-label">⑧時間単位年休</span>
                    <select
                      className="input"
                      value={form.hourlyLeaveHours}
                      onChange={(event) => set('hourlyLeaveHours', Number(event.target.value))}
                    >
                      {Array.from({ length: 9 }, (_, hour) => (
                        <option key={hour} value={hour}>
                          {hour}時間
                        </option>
                      ))}
                    </select>
                    <span className="field-hint">1時間単位・1日8時間まで</span>
                  </label>
                </div>
              </fieldset>
            </Card>
          ) : (
            <Notice tone="info">{form.workType}のため、勤務時刻の入力は不要です。</Notice>
          )}

          <Card title="業務内容" step={3} description="その日の主な業務と申し送りを入力します。">
            <div className="space-y-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <TextField
                  label="現場名"
                  value={form.siteName}
                  disabled={inputDisabled}
                  onChange={(value) => set('siteName', value)}
                />
                <TextField
                  label="主な業務"
                  value={form.taskNote}
                  required={works}
                  disabled={inputDisabled}
                  onChange={(value) => set('taskNote', value)}
                />
              </div>
              <details className="text-sm">
                <summary className="cursor-pointer text-ink-sub">備考・休憩修正を入力する</summary>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  {works && (
                    <NumberField
                      label="実働中の休憩を修正"
                      unit="分"
                      value={form.breakOverrideMinutes}
                      disabled={inputDisabled}
                      onChange={(value) => set('breakOverrideMinutes', value)}
                      hint="実働中に取得した休憩の合計。自動算出を上書きします"
                    />
                  )}
                  <TextField
                    label="備考"
                    value={form.remarks}
                    multiline
                    disabled={inputDisabled}
                    onChange={(value) => set('remarks', value)}
                  />
                </div>
              </details>
            </div>
          </Card>

          <IssueList
            issues={
              showIssues ? issues : issues.filter((issue) => issue.field && touched[issue.field])
            }
          />
          {times.hasErrors && (
            <Notice tone="danger">
              時刻の入力形式を確認してください。修正するまで保存されません。
            </Notice>
          )}

          {!readOnly && (
            <div className="action-bar">
              <span className="text-[0.78rem] text-ink-muted">
                {saving === 'copy'
                  ? 'コピー中です…'
                  : saving !== null
                    ? '保存中です…'
                    : saveError
                      ? '保存できませんでした。入力内容はこの画面に残っています'
                      : dirty
                        ? '保存していない変更があります'
                        : autoSavedAt
                          ? `自動保存：${autoSavedAt.getHours()}:${String(autoSavedAt.getMinutes()).padStart(2, '0')}`
                          : 'すべて保存されています'}
              </span>
              <span className="flex-1" />
              <button
                type="button"
                className="btn btn--secondary"
                disabled={saving !== null || state.loading}
                onClick={() => void save(false)}
              >
                下書き保存
              </button>
              <button
                type="button"
                className="btn btn--primary"
                disabled={saving !== null || state.loading}
                onClick={() => void save(true)}
              >
                内容を確認して提出
              </button>
              {showIssues && hasError && (
                <span className="w-full text-[0.8rem] text-danger-600">
                  入力エラーを解消すると提出できます
                </span>
              )}
            </div>
          )}
          {submitted && !state.data.closed && !(report?.locked ?? false) && (
            <div className="action-bar">
              <span className="text-[0.78rem] text-ink-muted">
                提出済み：{formatDateTime(report?.submittedAt ?? null)}
              </span>
              <span className="flex-1" />
              <button
                type="button"
                className="btn btn--secondary"
                disabled={saving !== null || state.loading}
                onClick={() => void withdraw()}
              >
                提出を取り消す
              </button>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <CalcPanel
            title="自動計算プレビュー"
            note="入力内容をもとに自動算出します。ここは編集できません。"
            provisional={me.provisional}
            main={[
              { label: '実働', value: formatDuration(computed.workMinutes) },
              { label: '移動', value: formatDuration(computed.travelMinutes) },
              { label: '稼働', value: formatDuration(computed.totalMinutes) },
            ]}
            rows={[
              { label: 'みなし補填', value: formatDuration(computed.deemedFillMinutes) },
              { label: '採用休憩', value: `${computed.breakWorkDeductMinutes}分` },
              { label: '日残業', value: formatDuration(computed.dailyOvertimeMinutes) },
              { label: '深夜時間', value: formatDuration(computed.nightMinutes) },
              { label: '食事手当', value: computed.mealAllowance ? '対象' : '対象外' },
            ]}
            footer={`計算版 ${me.engineVersion}・日種別：${dayType}`}
          >
            <div className="calc-status">
              {showIssues && hasError ? (
                <div>
                  <p className="font-semibold">入力エラーがあります</p>
                  <p>下の一覧を確認して修正してください。</p>
                </div>
              ) : (
                <div>
                  <p className="font-semibold">入力内容を確認できます</p>
                  <p>提出前に内容をご確認ください。</p>
                </div>
              )}
            </div>
          </CalcPanel>

          {computed.attendanceSpanMinutes > 0 && (
            <Card title="拘束時間">
              <p className="text-2xl font-bold">{formatDuration(computed.attendanceSpanMinutes)}</p>
              <p className="text-[0.78rem] text-ink-muted">最初の出発から最後の到着まで</p>
            </Card>
          )}
        </div>
      </div>

      {pendingEdit && (
        <Modal title="入力内容の変更を確認" onClose={() => setPendingEdit(null)}>
          <p className="mb-4 text-sm">{pendingEdit.message}</p>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="btn btn--secondary"
              onClick={() => setPendingEdit(null)}
            >
              戻る
            </button>
            <button type="button" className="btn btn--primary" onClick={pendingEdit.apply}>
              切り替える
            </button>
          </div>
        </Modal>
      )}
      {readOnly && (
        <p className="text-center text-[0.8rem] text-ink-muted">
          <button type="button" className="underline" onClick={() => navigate('/monthly-list')}>
            月間一覧に戻る
          </button>
        </p>
      )}
    </>
  );
}
